import { useEffect, useState } from "react";
import { FileText, Package, Zap, Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";

const tips = [
  "Creators who build a product within 7 days of signing up are 3x more likely to make their first sale.",
  "Adding a lead magnet to your bio can double your email signups in a week.",
  "Short-form scripts under 60 seconds get 2.5x more engagement than longer ones.",
  "Creators who use keyword research before scripting see 40% more views on average.",
  "Your first product doesn't need to be perfect — it needs to exist. Ship and iterate.",
  "Repurposing one long video into 5 shorts can 5x your content output without extra effort.",
];

export function DashboardStats() {
  const { user } = useAuth();
  const { balance, isUnlimited } = useCredits();
  const [scriptCount, setScriptCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [tip] = useState(() => tips[Math.floor(Math.random() * tips.length)]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: scripts } = await supabase
        .from("nexis_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .gte("created_at", startOfMonth.toISOString());

      const { count: products } = await supabase
        .from("monetization_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      setScriptCount(scripts || 0);
      setProductCount(products || 0);
    };

    fetchStats();
  }, [user]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="glass-card p-4 text-center">
          <FileText className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="font-display text-2xl font-bold text-foreground">{scriptCount}</p>
          <p className="text-xs text-muted-foreground">Scripts this month</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Package className="w-5 h-5 text-green-500 mx-auto mb-2" />
          <p className="font-display text-2xl font-bold text-foreground">{productCount}</p>
          <p className="text-xs text-muted-foreground">Products built</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Zap className="w-5 h-5 text-amber-500 mx-auto mb-2" />
          <p className="font-display text-2xl font-bold text-foreground">
            {isUnlimited ? "∞" : balance ?? 0}
          </p>
          <p className="text-xs text-muted-foreground">Credits left</p>
        </div>
      </div>

      {/* Weekly Tip */}
      <div className="glass-card p-4 border-amber-500/20 bg-amber-500/5">
        <div className="flex gap-3">
          <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-400 mb-1">Creator Tip</p>
            <p className="text-sm text-foreground leading-relaxed">{tip}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
